import React from "react";
import PropTypes from "prop-types";
import { makeStyles, fade } from "@material-ui/core/styles";
import { useTranslate } from "react-polyglot";
import { Box, IconButton, Typography } from "@material-ui/core";
import bookQueue from "../../../assets/ic_book-queue.svg";
import { ReactComponent as ArrowEnter } from "../../../assets/Icons/ic_arrow_enter.svg";
import { useAuth } from "../../../Providers/Auth/AuthProvider.jsx";
import { useBaseData } from "../../../Providers/Data/BaseDataProvider.jsx";
import FlexboxHorizontal from "../../../SharedComponents/FlexboxHorizontal.jsx";
import StyledCardBox from "../../../SharedComponents/StyledCardBox.jsx";
import LinearProgressWithLabel from "./LinearProgressWithLabel.jsx";
import { IS_APP_SKILLGEN } from "../../../constants";

const useStyles = makeStyles((theme) => ({
  mainBox: {
    marginBottom: theme.spacing(3),
  },
  cardHead: {
    color: theme.palette.primary.dark,
    textAlign: "left",
    "& .MuiCardHeader-content p": {
      fontWeight: 700,
    },
  },
  details: {
    alignItems: "start",
    justifyContent: "space-between",
    flexWrap: "nowrap",
  },
  logo: {
    width: 170,
    height: 170,
    borderRadius: 14,
    overflow: "hidden",
    flexShrink: 0,
    cursor: "pointer",
  },
  subjImg: {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "cover",
    objectPosition: "center",
  },
  infoBox: { flex: 1, margin: "16px 24px 0 16px", cursor: "pointer" },
  textBox: { height: 90, marginBottom: 16 },
  name: {
    fontSize: 18,
    fontWeight: 500,
    color: "#1B1C1C",
    display: "-webkit-box",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
  },
  description: {
    color: "#3D4853",
    fontSize: 14,
    textAlign: "left",
    wordBreak: "break-word",
    overflow: "hidden",
    display: "-webkit-box",
    "-webkit-line-clamp": 3,
    "-webkit-box-orient": "vertical",
  },
  iconButton: {
    marginTop: 50,
    padding: "10px 20px",
    backgroundColor: fade(theme.palette.primary.main, 0.08),
    borderRadius: 25,
    "& path": {
      stroke: theme.palette.primary.main,
    },
  },
  bookQueueIcon: {
    width: 20,
    height: 20,
    display: "block",
  },
  deckText: {
    color: "#54606B",
    fontSize: 14,
    marginLeft: theme.spacing(1),
  },
  actions: {
    padding: 0,
  },
  subjectBtn: {
    marginTop: 20,
    paddingLeft: 45,
    paddingRight: 45,
  },
  disabledBtn: {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.background.default,
    opacity: 1,
    "&::after": {
      content: '"$"',
      display: "block",
      height: 25,
      width: 25,
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      left: 10,
      fontSize: 18,
      fontWeight: 700,
    },
    "&.MuiInput-underline:after": {
      left: 10,
      borderBottom: "none",
    },
    "&.MuiInput-underline.Mui-focused:after": {
      transform: "translateY(-50%)",
    },
  },
}));

/**
 * @param {CompanySubject} subject
 * @param {function} onClick
 * @return {*}
 * @constructor
 */
const SubjectCard = ({ subject, onClick }) => {
  const t = useTranslate();
  const classes = useStyles();
  const { company2 } = useAuth();
  const { company } = useBaseData();

  const company_ = company || company2;

  /** */
  const pluralizeLessons = (count) => {
    if (count % 10 === 1 && count % 100 !== 11) return t("lessonsCount_one", { smart_count: count });
    if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100))
      return t("lessonsCount_few", { smart_count: count });
    return t("lessonsCount_many", { smart_count: count });
  };

  return (
    <StyledCardBox className={classes.mainBox}>
      <FlexboxHorizontal className={classes.details}>
        <div className={classes.logo} onClick={onClick}>
          <img
            className={classes.subjImg}
            src={subject.illustrationImageUrl || subject.illustrationImage?.url || company_?.logo.url}
            alt="logo"
          />
        </div>
        <Box className={classes.infoBox} onClick={onClick}>
          <Box className={classes.textBox}>
            <Typography className={classes.name}>{subject.name}</Typography>
            <Typography className={classes.description}>{subject.description || t("noDescription")}</Typography>
          </Box>
          {subject.courseProgress > 0 && <LinearProgressWithLabel value={subject.courseProgress} />}
          {!IS_APP_SKILLGEN && (
            <FlexboxHorizontal>
              <img className={classes.bookQueueIcon} src={bookQueue} alt="bookQueue" />
              <Typography className={classes.deckText}>{pluralizeLessons(subject.lessonsCount)}</Typography>
            </FlexboxHorizontal>
          )}
        </Box>

        <IconButton className={classes.iconButton} color="inherit" onClick={onClick}>
          <ArrowEnter />
        </IconButton>
      </FlexboxHorizontal>
    </StyledCardBox>
  );
};

SubjectCard.propTypes = {
  subject: PropTypes.object,
  onClick: PropTypes.func,
};

export default SubjectCard;
